import { useEffect, useState } from "react";
import { useAuthStore } from "@/lib/store/auth-enhanced";
import {
  promoLastShownKey,
  useShouldShowWhatsAppPromo,
} from "@/lib/whatsapp-promo/hooks/use-should-show-whatsapp-promo";
import { WhatsAppPromoDialog } from "@/lib/whatsapp-promo/whatsapp-promo-dialog";

type PromoToShow = NonNullable<ReturnType<typeof useShouldShowWhatsAppPromo>["promo"]>;

/**
 * Mounts the WhatsApp promo popup. When an active promo should be shown, it
 * opens the dialog once and records the timestamp in a per-user + per-event
 * localStorage key so it is not shown again within the 3-day cooldown.
 */
export function WhatsAppPromoProvider() {
  const { shouldShow, promo } = useShouldShowWhatsAppPromo();
  const user = useAuthStore((state) => state.user);
  const [open, setOpen] = useState(false);
  // Capture the promo so it stays available for rendering after we mark it shown.
  const [activePromo, setActivePromo] = useState<PromoToShow | null>(null);

  useEffect(() => {
    if (shouldShow && promo && user && !open) {
      setActivePromo(promo);
      setOpen(true);
      localStorage.setItem(promoLastShownKey(user.id, promo.id), String(Date.now()));
    }
  }, [shouldShow, promo, user, open]);

  if (!activePromo) {
    return null;
  }

  return <WhatsAppPromoDialog open={open} onOpenChange={setOpen} promo={activePromo} />;
}
